/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect } from 'react';

import { Box, Paper, Group, Image } from '@mantine/core';

// Styles
import classes from './meter-points.module.css';

// Components
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { Pagination } from '@components/atoms/Pagination';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// Apis
import { useMeterPointsApi } from '@features/resources/meter-points/api/get-meterpoints';

import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useSites } from 'shared/api/get-sites';
import { useProducers } from 'shared/api/get-producers';

// Datatable config
import { tableSchemaBase as columns } from '@features/resources/meter-points/meter-points.columns';

// Utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';

// Custom hook
import { DrawerMeterPointShowMore } from '@features/resources/meter-points/components/DrawerMeterPointShowMore';
import { useMeterPoint } from '@features/resources/meter-points/hooks/useMeterPoint';
import { useRemove } from '@features/resources/meter-points/api/remove';
import { ConfirmationModal } from '@components/molecules';
import { TTargetColumn } from '@global-types/index';
import { UpdateMeterPoints } from '@features/resources/meter-points/components/UpdateMeterPoints';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeterPoints = () => {
	const { t } = useTranslation();

	const {
		containerRef,
		meterPointsPage,
		meterPointsPageSize,
		queryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		showScrollFilters,
		handleapplyprojectfilter,
		ref,
		handlerowclicked,
		handlerowviewmore,
		handlerowremoveitem,
		handlechangesortstatus,
		handleselectedrecordchange,
		handlePaginationChangeSize,
		handleChangePagination,
		handleapplysitefilter,
		handleapplyproducerfilter,
		openDrawer,
		handledeleteconversionfactor,
		queryParamsProducers,
		resetFilterProjects,
		setResetFilterProjects,
		setQueryParamsProjects,
		meterPointsSortStatus,
		meterPointsSelectedRow,
		handleCloseDrawerShowMoreMeterPoint,
		meterPointsSortParams,
		meterPointsQueryParams,
		setMeterPointsQueryParams,
		meterPointsOpenConfirmationModalDelete,
		handleclosedeleteconfirmationmodal,
		handledeleteconfirmationmodalconfirm,
		setOpenDrawerMeterPointForm,
		meterpointsDatatableOptions,
		setMeterpointsDatatableOptions,
		openUpdateMeterpointDrawer,
		setOpenUpdateMeterpointDrawer,
		handleeditmeterpoint,
		setOpenDrawer,
	} = useMeterPoint();

	// APIs
	const {
		data: projects,
		isFetched: isFetchedMeasurabilities,
	} = useMeasurabilities(queryParamsProducers);

	const { data: sites } = useSites(queryParamsProjects);

	const { data: producers } = useProducers();

	const remove = useRemove(
		meterPointsPage,
		meterPointsPageSize
	);

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useMeterPointsApi(
		meterPointsPage,
		meterPointsPageSize,
		meterPointsQueryParams,
		meterPointsSortParams
	);

	useEffect(() => {
		if (
			isFetchedMeasurabilities &&
			queryParamsProducers &&
			queryParamsProducers.entity &&
			queryParamsProducers.entity.length !== 0
		) {
			const queryParamsProjects = {
				project: projects.map(
					(_project: { id: string }) => _project.id
				),
				site: [],
			};
			setQueryParamsProjects(queryParamsProjects);
			setMeterPointsQueryParams({
				...meterPointsQueryParams,
				project: projects.map(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(_project: { id: string }) => _project.id
				),
			});
		}
	}, [isFetchedMeasurabilities, queryParamsProducers]);

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/**************************** Drawer show more meterPoint ****************************/}
			<DrawerMeterPointShowMore
				opened={openDrawer}
				handledelete={handledeleteconversionfactor}
				onClose={handleCloseDrawerShowMoreMeterPoint}
				position={'right'}
				data={meterPointsSelectedRow}
				handleupdate={() => {
					setOpenUpdateMeterpointDrawer(true);
					setOpenDrawer(false);
				}}
			/>

			{/**************************** Add meterPoints ****************************/}
			<UpdateMeterPoints
				onClose={() => {
					setOpenUpdateMeterpointDrawer(false);
				}}
				opened={openUpdateMeterpointDrawer}
			/>

			{/**************************** Delete confirmation modal ****************************/}

			<ConfirmationModal
				title={t('meterPoints.modal.delete.title')}
				description={t(
					'meterPoints.modal.delete.description'
				)}
				onConfirm={() =>
					handledeleteconfirmationmodalconfirm(remove)
				}
				onClose={handleclosedeleteconfirmationmodal}
				opened={
					meterPointsOpenConfirmationModalDelete &&
					meterPointsOpenConfirmationModalDelete.open
				}
			/>

			{/**************************** Content ****************************/}
			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
											}}
											onApply={handleapplyproducerfilter}
											label={t('global.producer')}
											size="lg"
											options={convertArrayToLabelValue(
												producers
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProducer'
											)}
											placeholder={t(
												'filters.placeholders.selectProducer'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterProjects}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
												setResetFilterSites(_isReset);
											}}
											onApply={handleapplyprojectfilter}
											label={t('global.projects')}
											size="lg"
											options={convertArrayToLabelValue(
												projects
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProject'
											)}
											placeholder={t(
												'filters.placeholders.selectProject'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterSites}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterSites(_isReset);
											}}
											onApply={handleapplysitefilter}
											label={t('global.sites')}
											size="lg"
											options={convertArrayToLabelValue(
												sites
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchSite'
											)}
											placeholder={t(
												'filters.placeholders.selectSite'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.METER_POINTS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={() => {
										setOpenDrawerMeterPointForm(true);
									}}
								>
									{t('meterPoints.addMeterPoint')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={() => {
										setOpenDrawerMeterPointForm(true);
									}}
								>
									{t('meterPoints.addMeterPoint')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setMeterpointsDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={meterpointsDatatableOptions}
						data={data}
						onRowClick={handlerowclicked}
						onViewMore={handlerowviewmore}
						onRemoveItem={handlerowremoveitem}
						onEditItem={handleeditmeterpoint}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						onSelectedRecordsChange={
							handleselectedrecordchange
						}
						onSortStatusChange={handlechangesortstatus}
						sortstatus={meterPointsSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={meterPointsPageSize}
					totalitems={totalItems}
					activepage={meterPointsPage}
					handlechangesize={handlePaginationChangeSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default MeterPoints;
